import type { TicketSource } from '@vd/types';
import { Chrome, Code2, Link2, Bot, PanelRight, PencilLine, Users, Video } from 'lucide-react';
import styles from './SourceBadge.module.css';

/**
 * Where a ticket came from.
 *
 * Worth surfacing because provenance changes how you read the ticket. An
 * extension capture was filed by a colleague who could be asked a follow-up
 * question; a widget report came from a stranger on the customer's site who
 * cannot be reached unless they left an email. Triage priority, tone of
 * reply, and how much you trust the reproduction steps all differ.
 *
 * `undefined` is treated as `extension`: responses from an api build older
 * than the provenance columns carry no source, and every ticket that
 * predates them came through the extension.
 */

interface SourceMeta {
  label: string;
  Icon: typeof Chrome;
  /** Longer explanation for the tooltip / detail view. */
  description: string;
}

const SOURCES: Record<TicketSource, SourceMeta> = {
  extension: {
    label: 'Extension',
    Icon: Chrome,
    description: 'Captured with the Toado browser extension by a team member.',
  },
  widget: {
    label: 'Widget',
    Icon: Code2,
    description: 'Sent by a visitor through the report widget embedded on your site.',
  },
  public_link: {
    label: 'Report link',
    Icon: Link2,
    description: 'Sent through your shareable Toado report page.',
  },
  team_snippet: {
    label: 'Team snippet',
    Icon: Users,
    description: 'Captured by a signed-in team member through the embedded snippet.',
  },
  mcp: {
    label: 'AI agent',
    Icon: Bot,
    description: 'Created by an AI agent over MCP.',
  },
  manual: {
    label: 'Manual',
    Icon: PencilLine,
    description: 'Typed directly into Toado.',
  },
  recording: {
    label: 'Walkthrough',
    Icon: Video,
    description: 'Created from a narrated Toado walkthrough recording.',
  },
  team_panel: {
    label: 'Team panel',
    Icon: PanelRight,
    description: 'Pinned by a signed-in team member through the team panel on your site.',
  },
};

export function sourceMeta(source: TicketSource | undefined): SourceMeta {
  return SOURCES[source ?? 'extension'] ?? SOURCES.extension;
}

/** Is this ticket from someone outside the workspace? */
export function isExternalSource(source: TicketSource | undefined): boolean {
  return source === 'widget' || source === 'public_link';
}

export function SourceBadge({
  source, showLabel = true,
}: { source: TicketSource | undefined; showLabel?: boolean }) {
  const meta = sourceMeta(source);
  const { Icon } = meta;
  return (
    <span
      className={isExternalSource(source) ? styles.badgeExternal : styles.badge}
      title={meta.description}
    >
      <Icon size={12} aria-hidden />
      {/* The label is hidden visually on dense surfaces but kept for
          assistive tech — an icon-only provenance marker is meaningless
          to a screen reader. */}
      <span className={showLabel ? undefined : styles.srOnly}>{meta.label}</span>
    </span>
  );
}
